import React, { useState, useCallback, useMemo, forwardRef } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import PropertyRefundabilitySelector from './PropertyRefundabilitySelector'
import PropertyRateOption from './PropertyRateOption'
import PropertyCancellationPolicy from './PropertyCancellationPolicy'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LargeContentToggle from 'components/Common/LargeContentToggle'
import OfferPagePackageInclusionsList from 'components/OfferPage/OfferPagePackageInclusionsList'
import OfferInclusionItem from 'components/Common/Inclusions/OfferInclusionItem'
import BodyText from 'LuxKit/Typography/BodyText'
import LuxPlusInclusions from 'luxPlus/components/LuxPlusInclusions'
import Group from 'components/utils/Group'
import AccommodationPackageTileDetails from 'components/AccommodationKit/AccommodationPackageTileDetails'
import { useAppSelector } from 'hooks/reduxHooks'
import { isLEStaffOrAdmin, userIsSpoofed } from 'selectors/accountSelectors'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_HOTEL, OFFER_TYPE_LAST_MINUTE } from 'constants/offer'

const MergedContainer = styled.div`
  position: relative;
  padding: ${rem(20)};
  display: grid;
  grid-template-columns: 3fr 2fr;
  column-gap: ${rem(32)};
`

function getSingleOptionStaffRateLabel(
  staffRateLabelType: 'lpp' | 'lpc' | undefined,
  showStaffLabels: boolean,
  rateServiceType: App.Property.ServiceRateDetails['type'],
  rateName?: string,
  fallbackPackageName?: string,
) {
  if (!showStaffLabels) {
    return undefined
  }

  if (rateServiceType === 'bedbank') {
    return 'Bedbank rate (label only shows for staff)'
  }

  if (!staffRateLabelType) {
    return undefined
  }

  const packageName = rateName || fallbackPackageName
  const rateTypeLabel = staffRateLabelType === 'lpp' ? 'LPP' : 'LPC'

  if (!packageName) {
    return `${rateTypeLabel} rate (label only shows for staff)`
  }

  return `${rateTypeLabel} rate - ${packageName} (label only shows for staff)`
}

interface Props {
  lpcOffer?: App.HotelOffer
  rates: Array<App.PropertyRate>
  optionNumber: number
  showOptionLabel: boolean
  roomListId: string
  roomPosition: number
  rateListId: string
  propertyTimezone: string
}

/**
 * Merged variant that combines property rates with radio button selection.
 *
 * New UI structure:
 * - Radio selector for rates (by rate ID)
 * - Inclusions list
 * - Pricing and CTA buttons (reusing PropertyRateOption)
 */
const PropertyRateOptionMerged = forwardRef<HTMLDivElement, Props>(function PropertyRateOptionMerged(props, ref) {
  const {
    lpcOffer,
    rates,
    optionNumber,
    showOptionLabel,
    roomListId,
    roomPosition,
    rateListId,
    propertyTimezone,
  } = props

  // Default to first rate in the group
  const defaultRateId = rates[0]?.rateId
  const [selectedRateId, setSelectedRateId] = useState<string | undefined>(defaultRateId)

  const activeRate = useMemo(() => {
    return rates.find(rate => rate.rateId === selectedRateId) || rates[0]
  }, [rates, selectedRateId])

  const handleRateChange = useCallback((rateId: string) => {
    setSelectedRateId(rateId)
  }, [])

  const hasChoice = rates.length > 1
  const isStaff = useAppSelector(isLEStaffOrAdmin)
  const isSpoofed = useAppSelector(userIsSpoofed)
  const shouldShowStaffLabels = useMemo(() => {
    return isStaff && !isSpoofed
  }, [isStaff, isSpoofed])
  // Use rate.name as package name since the same packageId can correspond to multiple different names
  const ratePackageNameByRateId = useMemo(() => {
    return rates.reduce<Record<string, string>>((accumulator, rate) => {
      if (rate.service.type !== 'lpc' || !rate.name) {
        return accumulator
      }
      accumulator[rate.rateId] = rate.name
      return accumulator
    }, {})
  }, [rates])
  const staffRateLabelType = useMemo(() => {
    if (!shouldShowStaffLabels) {
      return undefined
    }

    // Connectivity commercial uses staff labels to confirm rate source for price parity checks and partner test bookings.
    if (lpcOffer?.type === OFFER_TYPE_LAST_MINUTE) {
      return 'lpp'
    }

    if (lpcOffer?.type === OFFER_TYPE_ALWAYS_ON || lpcOffer?.type === OFFER_TYPE_HOTEL) {
      return 'lpc'
    }

    return undefined
  }, [lpcOffer?.type, shouldShowStaffLabels])
  const activeRatePackageName = ratePackageNameByRateId[activeRate?.rateId ?? '']
  const singleOptionStaffRateLabel = useMemo(() => {
    return getSingleOptionStaffRateLabel(
      staffRateLabelType,
      shouldShowStaffLabels,
      activeRate?.service.type ?? 'lpc',
      activeRate?.name,
      activeRatePackageName,
    )
  }, [
    activeRate?.name,
    activeRate?.service.type,
    activeRatePackageName,
    shouldShowStaffLabels,
    staffRateLabelType,
  ])

  const headlineCancellationPolicyStart = useMemo(
    () => activeRate?.headlineCancellationPolicyStart ? new Date(activeRate.headlineCancellationPolicyStart) : undefined,
    [activeRate?.headlineCancellationPolicyStart],
  )

  const cancellationPolicy = useMemo(() => {
    if (!activeRate) return null

    return (
      <PropertyCancellationPolicy
        propertyTimeZone={propertyTimezone}
        headlineCancellationPolicyType={activeRate.headlineCancellationPolicyType}
        headlineCancellationPolicyStart={headlineCancellationPolicyStart}
        fullyRefundableUntil={activeRate.fullyRefundableUntil}
        cancellationPolicies={activeRate.cancellationPolicies}
        currency={activeRate.currency}
        shouldClarifyRefundIsHotelOnly={activeRate.includesFlights}
      />
    )
  }, [
    activeRate,
    propertyTimezone,
    headlineCancellationPolicyStart,
  ])

  if (!activeRate) {
    return null
  }

  const packageNumber = showOptionLabel ? optionNumber : undefined

  return (
    <MergedContainer ref={ref} data-testid="merged-property-rate-option">
      <VerticalSpacer gap={hasChoice ? 20 : 12}>
        {packageNumber != null && <AccommodationPackageTileDetails
          packageNumber={packageNumber}
        />}

        {hasChoice && (
          <PropertyRefundabilitySelector
            rates={rates}
            selectedRateId={selectedRateId}
            onChange={handleRateChange}
            propertyTimezone={propertyTimezone}
            staffRateLabelType={staffRateLabelType}
            ratePackageNameByRateId={ratePackageNameByRateId}
            showStaffLabels={shouldShowStaffLabels}
          />
        )}

        {!hasChoice && (
          <Group direction="vertical" gap={4} noDisplayWhenEmpty>
            {cancellationPolicy}
            {singleOptionStaffRateLabel && (
              <BodyText variant="medium" colour="neutral-three">
                {singleOptionStaffRateLabel}
              </BodyText>
            )}
          </Group>
        )}

        <LargeContentToggle
          maxHeight={208}
          showMoreText="View more inclusions"
          showLessText="View less inclusions"
        >
          <VerticalSpacer gap={12}>
            {activeRate.luxPlusInclusions.length > 0 && <LuxPlusInclusions
              luxPlusInclusions={activeRate.luxPlusInclusions}
              accommodationOffer={lpcOffer}
            />}
            {activeRate.bonusInclusions.length > 0 && <OfferPagePackageInclusionsList title="bonus">
              {activeRate.bonusInclusions.map(inclusion => <OfferInclusionItem key={inclusion.id} inclusion={inclusion} />)}
            </OfferPagePackageInclusionsList>}
            {activeRate.inclusions.length > 0 && <VerticalSpacer gap={4}>
              {activeRate.inclusions.map(inclusion => <OfferInclusionItem key={inclusion.id} inclusion={inclusion} />)}
            </VerticalSpacer>}
          </VerticalSpacer>
        </LargeContentToggle>
      </VerticalSpacer>

      <PropertyRateOption
        lpcOffer={lpcOffer}
        rate={activeRate}
        optionNumber={activeRate.defaultOrder + 1}
        showOptionLabel={false}
        roomListId={roomListId}
        roomPosition={roomPosition}
        rateListId={rateListId}
      />
    </MergedContainer>
  )
})

PropertyRateOptionMerged.displayName = 'PropertyRateOptionMerged'

export default PropertyRateOptionMerged
